import React from 'react';
import {
  Button,
  Checkbox,
  Form,
  Modal,
  Select,
  Tabs,
  Typography,
} from '@crisellevaldez/speculo-ui';
import { useUniqueContactTags } from '../../../hooks/contacts/useUniqueContactTags';
import { SearchableSelect } from '../../common/SearchableSelect';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { buildUserOptions } from '../../../utils/userOptions';
import { useFlywheelEnabled, useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useOrgFlowOptions } from '../../../hooks/graphql/flows/useOrgFlowOptions';
import { LG_BREAKPOINT, useMediaQuery } from '../../../hooks/useMediaQuery';
import { AdvancedFilterBuilder } from './AdvancedFilterBuilder';
import { FILTER_COUNT_BADGE_CLASS } from '../../common/FilterButton';
import {
  countActiveConditions,
  emptyAdvancedFilter,
  type ContactAdvancedFilter,
} from '../../../types/contactFilter.types';
import { EMPTY_FILTER_STATE, type FilterState } from './filterState';

// Note: Recent Outcome filter is exposed as a quick-filter dropdown in
// ContactTableHeader (CONTACT_OUTCOMES list lives there). Keep the outcome
// state plumbed through this modal so callers' existing values pass through
// unchanged when Apply is clicked, but no UI is rendered here for it.

const CONSENT_CHANNELS: { key: 'voiceConsent' | 'smsConsent' | 'emailConsent'; label: string }[] = [
  { key: 'voiceConsent', label: 'Calls' },
  { key: 'smsConsent', label: 'SMS' },
  { key: 'emailConsent', label: 'Email' },
];

/** SMS is a flywheel-only channel, so it is not a consent an org without it filters on. */
const NON_SMS_CONSENT_CHANNELS = CONSENT_CHANNELS.filter((c) => c.key !== 'smsConsent');

interface FilterPanelProps {
  open: boolean;
  onClose: () => void;
  /** The applied filters. Controlled: every edit calls `onChange` immediately. */
  value: FilterState;
  onChange: (filters: FilterState) => void;
}

/** Panel width when open. The condition grid needs about this much. */
const PANEL_WIDTH = 'lg:w-[27rem]';

/**
 * Filters for the contact list, as a panel beside the table rather than over
 * it — the table narrows to make room and keeps updating as filters change.
 *
 * Laid out inline instead of in a Drawer on purpose: a drawer is fixed and
 * floats above the page, so the rows behind it stay hidden however little it
 * dims them.
 *
 * Controlled rather than draft-and-apply. There is no Apply button, so there
 * is no local copy of the filters to keep in sync — the effect that used to
 * mirror `initialFilters` into 15 useStates is gone with it. The query itself
 * is debounced in `useContacts`, so a settled change costs one request.
 */
export const FilterPanel: React.FC<FilterPanelProps> = ({ open, onClose, value, onChange }) => {
  const {
    data: tags = [],
    isLoading: isTagsLoading,
    hasMore: tagsHasMore,
    loadMore: tagsLoadMore,
    setSearch: tagsSetSearch,
  } = useUniqueContactTags(open);
  const { users, loading: usersLoading } = useOrganizationUsers();
  const flywheelEnabled = useFlywheelEnabled();
  const consentChannels = flywheelEnabled ? CONSENT_CHANNELS : NON_SMS_CONSENT_CHANNELS;
  const { organization } = useOrganizationStore();
  const {
    options: flowOptions,
    flowTypes: availableFlowTypes,
    isLoading: isFlowOptionsLoading,
  } = useOrgFlowOptions(organization?.id, open);
  // Read straight from the controlled value, and write through a patch. The
  // names match what the JSX below already used when these were useStates.
  const patch = (fields: Partial<FilterState>) => onChange({ ...value, ...fields });

  const selectedTags = value.selectedTags;
  const matchAllTags = value.matchAllTags ?? false;
  const leadSource = value.leadSource;
  const teamMember = value.teamMember;
  const leadIntent = value.leadIntent ?? [];
  const hasEalertsRegistered = value.has_ealerts_registered ?? null;
  const flywheelOptOut = value.flywheelOptOut ?? null;
  const aiPaused = value.aiPaused ?? null;
  const flowIds = value.flowIds ?? [];
  const flowTypes = value.flowTypes ?? [];
  const advancedFilter = value.advancedFilter ?? emptyAdvancedFilter();
  const consent: Record<string, string | null> = {
    voiceConsent: value.voiceConsent ?? null,
    smsConsent: value.smsConsent ?? null,
    emailConsent: value.emailConsent ?? null,
  };

  const setSelectedTags = (next: string[]) => patch({ selectedTags: next });
  const setMatchAllTags = (next: boolean) => patch({ matchAllTags: next });
  const setLeadSource = (next: string[]) => patch({ leadSource: next });
  const setTeamMember = (next: string | undefined) => patch({ teamMember: next });
  const setLeadIntent = (next: string[]) => patch({ leadIntent: next });
  const setHasEalertsRegistered = (next: boolean | null) => patch({ has_ealerts_registered: next });
  const setFlywheelOptOut = (next: boolean | null) => patch({ flywheelOptOut: next });
  const setAiPaused = (next: boolean | null) => patch({ aiPaused: next });
  const setFlowIds = (next: string[]) => patch({ flowIds: next });
  const setFlowTypes = (next: string[]) => patch({ flowTypes: next });
  const setAdvancedFilter = (next: ContactAdvancedFilter) => patch({ advancedFilter: next });
  const setConsent = (next: Record<string, string | null>) =>
    patch({
      voiceConsent: next.voiceConsent ?? null,
      smsConsent: next.smsConsent ?? null,
      emailConsent: next.emailConsent ?? null,
    });

  const [activeTab, setActiveTab] = React.useState<'quick' | 'custom'>('quick');
  const advancedConditionCount = countActiveConditions(advancedFilter);

  // The two tab bodies are ANDed together server-side, so each tab shows its
  // own active count — otherwise a filter set on the tab you are not looking
  // at silently narrows the result and reads as missing data.
  const quickFilterCount = [
    selectedTags.length > 0,
    Boolean(teamMember),
    leadSource.length > 0,
    (leadIntent ?? []).length > 0,
    flowIds.length > 0,
    flowTypes.length > 0,
    Boolean(consent.voiceConsent),
    Boolean(consent.smsConsent),
    Boolean(consent.emailConsent),
    hasEalertsRegistered === true,
    aiPaused === true,
    flywheelOptOut === false,
  ].filter(Boolean).length;

  const TAB_TRIGGERS: { value: 'quick' | 'custom'; label: string; count: number }[] = [
    { value: 'quick', label: 'Quick filters', count: quickFilterCount },
    { value: 'custom', label: 'Custom conditions', count: advancedConditionCount },
  ];

  // Applies immediately, like every other edit — there is no Apply button to
  // press afterwards, and the drawer stays open because Reset is usually the
  // start of building a different filter rather than the end of the task.
  const handleReset = () => onChange(EMPTY_FILTER_STATE);

  const isDesktop = useMediaQuery(LG_BREAKPOINT);
  // Beside the table the panel stretches to the row, so the tab body takes
  // whatever is left after the header and footer — which needs the height
  // carried down through Tabs and the active panel, both of which are plain
  // flex children of the root. In the modal there is no row to match, so the
  // body is simply bounded instead.
  const bodyHeight = isDesktop ? 'h-full' : 'h-[min(34rem,68vh)]';
  const tabsClass = isDesktop ? 'flex h-full flex-col' : undefined;
  const tabsListClass = isDesktop ? 'shrink-0' : undefined;
  const tabsPanelClass = isDesktop ? 'min-h-0 flex-1' : undefined;

  // Built once and placed in whichever shell the viewport calls for. Rendering
  // both and hiding one with CSS would mount the builder twice and re-run its
  // catalogue, tag and user queries.
  const tabs = (
    <Tabs
      className={tabsClass}
      value={activeTab}
      onChange={(value) => setActiveTab(value as 'quick' | 'custom')}
    >
      <Tabs.List className={tabsListClass}>
        <div className="flex gap-2 md:gap-4 border-b border-gray-200 dark:border-white/10">
          {TAB_TRIGGERS.map((tab) => (
            <button
              key={tab.value}
              role="tab"
              aria-selected={activeTab === tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-2 py-2 text-sm font-medium md:px-4 ${
                activeTab === tab.value
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {/* Both tabs are ANDed server-side, so the count keeps a
                      filter set on the hidden tab from looking like no filter.
                      Same pill as the Filter button that opened this modal. */}
              {tab.count > 0 && <span className={FILTER_COUNT_BADGE_CLASS}>{tab.count}</span>}
            </button>
          ))}
        </div>
      </Tabs.List>

      {/* Both panels are the same fixed height and scroll internally, so
              the dialog does not resize on tab change or as conditions are
              added. Capped against the viewport so the footer stays reachable
              on a short window. */}
      <Tabs.Panel value="quick" className={tabsPanelClass}>
        <div className={`space-y-5 overflow-y-auto scrollbar-custom pr-1 pt-2 ${bodyHeight}`}>
          <div className="flex flex-col gap-4">
            <Form.Control className="flex-1">
              <Form.Label>Select Tag(s)</Form.Label>
              <SearchableSelect
                placeholder="Select tags"
                value={selectedTags}
                onChange={(value) => setSelectedTags(value)}
                options={tags.map((tag) => ({ value: tag, label: tag }))}
                isLoading={isTagsLoading}
                hasMore={tagsHasMore}
                onLoadMore={tagsLoadMore}
                onSearch={tagsSetSearch}
                searchPlaceholder="Search tags..."
                multiple
              />
              <div className="flex items-center gap-2 mt-2">
                <Checkbox
                  id="match_all_tags"
                  checked={matchAllTags}
                  onChange={(e) => setMatchAllTags(e.target.checked)}
                />
                <Form.Label
                  htmlFor="match_all_tags"
                  className="cursor-pointer flex items-center my-0 text-xs text-muted-foreground"
                >
                  Match all selected tags (AND). Otherwise any (OR).
                </Form.Label>
              </div>
            </Form.Control>

            <Form.Control className="flex-1">
              <Form.Label>Assigned To</Form.Label>
              <Select
                placeholder="Select team member"
                value={teamMember}
                onChange={(value) => setTeamMember(value as string)}
                options={buildUserOptions(users)}
                isLoading={usersLoading}
                searchable
                className="py-2.5"
              />
            </Form.Control>
          </div>

          <div className="flex flex-col gap-4">
            <Form.Control className="flex-1">
              <Form.Label>Lead Source</Form.Label>
              <Select
                placeholder="Select options"
                value={leadSource}
                onChange={(value) => setLeadSource(value as string[])}
                options={[
                  { value: 'Email', label: 'Email' },
                  { value: 'PPC', label: 'PPC' },
                  { value: 'Organic', label: 'Organic' },
                ]}
                multiple
                searchable
              />
            </Form.Control>

            <Form.Control className="flex-1">
              <Form.Label>Intent</Form.Label>
              <Select
                placeholder="Select intent"
                value={leadIntent}
                onChange={(value) => setLeadIntent(value as string[])}
                options={[
                  { value: 'Buyer', label: 'Buyer' },
                  { value: 'Seller', label: 'Seller' },
                ]}
                multiple
                searchable
              />
            </Form.Control>
          </div>

          <div className="flex flex-col gap-4">
            <Form.Control className="flex-1">
              <Form.Label>Flow</Form.Label>
              <Select
                placeholder="Select flow(s)"
                value={flowIds}
                onChange={(value) => setFlowIds(value as string[])}
                options={flowOptions}
                isLoading={isFlowOptionsLoading}
                multiple
                searchable
              />
            </Form.Control>

            <Form.Control className="flex-1">
              <Form.Label>Flow Type</Form.Label>
              <Select
                placeholder="Select flow type(s)"
                value={flowTypes}
                onChange={(value) => setFlowTypes(value as string[])}
                options={availableFlowTypes.map((t) => ({ value: t, label: t }))}
                isLoading={isFlowOptionsLoading}
                multiple
                searchable
              />
            </Form.Control>
          </div>

          {/* Per-channel consent (SPEC-2117) — matches the Opt In column on
              each row. Carded, because the unlabelled toggle grid that follows
              used to read as more consent channels. */}
          <div className="rounded-lg border border-foreground/30 p-3 shadow-sm">
            <Form.Label className="mb-2">Opted in</Form.Label>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {consentChannels.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox
                    id={`consent_${key}`}
                    checked={consent[key] === 'opted_in'}
                    onChange={(e) =>
                      setConsent({
                        ...consent,
                        [key]: e.target.checked ? 'opted_in' : null,
                      })
                    }
                  />
                  <Form.Label
                    htmlFor={`consent_${key}`}
                    className="cursor-pointer flex items-center my-0"
                  >
                    {label}
                  </Form.Label>
                </div>
              ))}
            </div>
          </div>

          {/* Boolean contact states. Labelled and carded: unlabelled and
              flush under "Opted in", these four looked like consent options.
              Laid out 2-per-row on sm+ so each box sits on its own line. */}
          <div className="rounded-lg border border-foreground/30 p-3 shadow-sm">
            <Form.Label className="mb-2">Status</Form.Label>
            <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="has_ealerts_registered"
                  checked={hasEalertsRegistered === true}
                  onChange={(e) => setHasEalertsRegistered(e.target.checked)}
                />
                <Form.Label
                  htmlFor="has_ealerts_registered"
                  className="cursor-pointer flex items-center my-0"
                >
                  E-Alert Subscription Active
                </Form.Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="ai_paused"
                  checked={aiPaused === true}
                  onChange={(e) => setAiPaused(e.target.checked ? true : null)}
                />
                <Form.Label htmlFor="ai_paused" className="cursor-pointer flex items-center my-0">
                  AI Paused
                </Form.Label>
              </div>
              {flywheelEnabled && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="flywheel_enrolled"
                    checked={flywheelOptOut === false}
                    onChange={(e) => setFlywheelOptOut(e.target.checked ? false : null)}
                  />
                  <Form.Label
                    htmlFor="flywheel_enrolled"
                    className="cursor-pointer flex items-center my-0"
                  >
                    In Follow-up Flywheel
                  </Form.Label>
                </div>
              )}
            </div>
          </div>
        </div>
      </Tabs.Panel>

      {/* Everything in the other tab is a fixed filter. This one expresses
              the comparisons those cannot — not equal, greater/less than, is
              empty — joined by a single AND or OR. Compiled and run
              server-side, and ANDed with whatever the quick tab has set. */}
      <Tabs.Panel value="custom" className={tabsPanelClass}>
        <div className={`space-y-5 overflow-y-auto scrollbar-custom pr-1 pt-2 ${bodyHeight}`}>
          {/* `block` because the small variant renders inline, where a
                  bottom margin would be ignored. */}
          <Typography variant="small" className="mb-5 block text-muted-foreground">
            Pick a field, a comparison, and a value. Choose whether contacts have to match every
            condition or any one of them.
          </Typography>
          <AdvancedFilterBuilder
            value={advancedFilter}
            onChange={setAdvancedFilter}
            quickFilterCount={quickFilterCount}
            enabled={open}
            organizationId={organization?.id}
          />
        </div>
      </Tabs.Panel>
    </Tabs>
  );

  const resetButton = (
    <Button variant="secondary" onClick={handleReset}>
      Reset
    </Button>
  );

  // Below lg there is no room to sit beside the table, so the same filters
  // open as a modal instead of squeezing the rows to nothing.
  if (!isDesktop) {
    return (
      <Modal isOpen={open} onClose={onClose} size="xl">
        <Modal.Header>
          <Typography variant="h5">Filters</Typography>
        </Modal.Header>
        <Modal.Body>{tabs}</Modal.Body>
        <Modal.Footer>{resetButton}</Modal.Footer>
      </Modal>
    );
  }

  return (
    // The wrapper animates its width so the panel slides the table aside; the
    // inner column keeps a fixed width so nothing reflows mid-animation, and
    // the body is unmounted when closed so the collapsed wrapper holds no
    // height. h-full makes the panel match the column beside it — the flex row
    // stretches both, so it ends level with the bottom of the table.
    <div
      // The panel's content is absolutely positioned inside this wrapper, so it
      // contributes no height. That is what lets self-stretch mean "as tall as
      // the table": a flex row sizes to its tallest child, so while the cards
      // were in flow a tall stack drove the row and the panel stretched into
      // all of it, extending the page. Out of flow, the row is sized by the
      // table alone and the cards scroll within whatever that gives us.
      // min-h keeps it usable when the table is only a couple of rows.
      // No width/margin transition. Animating those animates *layout*, so the
      // table beside it re-flowed every frame — its columns are sized, so the
      // recalculation showed up as the whole table wiggling for 300ms. Opening
      // instantly is worth more than the slide.
      className={`relative shrink-0 self-stretch overflow-hidden lg:min-h-[24rem] ${
        open ? `w-full lg:ml-4 ${PANEL_WIDTH}` : 'w-0'
      }`}
    >
      {open && (
        <aside
          // Absolute so the card stack cannot grow the wrapper. Width is kept
          // explicit rather than using inset-x-0 so the content does not
          // reflow while the wrapper animates open.
          className={`absolute inset-y-0 left-0 flex w-full flex-col ${PANEL_WIDTH} rounded-lg border border-foreground/30 bg-white shadow-sm dark:bg-transparent`}
          aria-label="Contact filters"
        >
          <div className="min-h-0 flex-1 px-4 xl:px-5 py-3">{tabs}</div>
          <div className="flex shrink-0 justify-end gap-3 border-t border-foreground/30 px-4 py-3">
            {resetButton}
          </div>
        </aside>
      )}
    </div>
  );
};
